import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  App,
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Tabs,
  Typography,
} from 'antd';
import {
  FilePdfOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  AppstoreOutlined,
  SearchOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import { PageHeader, PageContainer, DevFilePathBadge } from '../components/common';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';

const { Text, Link } = Typography;

type LabelTargetTab = 'location' | 'equipment' | 'part' | 'sheets';
type PartQrKind = 'part' | 'partCategoryOrder';

type QrLabelTemplate = {
  id: string;
  name: string;
  paper: { wMm: number; hMm: number };
  grid: { cols: number; rows: number };
  label: {
    wMm: number;
    hMm: number;
    marginTopMm: number;
    marginLeftMm: number;
    gapXMm: number;
    gapYMm: number;
  };
  calibration: { xOffsetMm: number; yOffsetMm: number };
  labelsPerPage: number;
};

type LocationRow = {
  id: string;
  displayId: string;
  name: string;
};

type EquipmentRow = {
  id: string;
  displayId: string;
  name: string;
};

type PartRow = {
  id: string;
  displayId: string;
  name: string;
};

type PartCategoryRow = {
  id: string;
  categoryLabel: string;
  description?: string | null;
  displayOrder?: number | null;
};

type PrintSheetGroup = {
  title: string;
  items: Array<{ label: string; url: string }>;
};

const VALID_TABS: LabelTargetTab[] = ['location', 'equipment', 'part', 'sheets'];
const PART_CATEGORY_ORDER_HASH = 'part-category-order';

const PRINT_SHEET_GROUPS: PrintSheetGroup[] = [
  {
    title: 'A4 12面（2×6） 84×42mm',
    items: [
      { label: 'エーワン 28729', url: 'https://www.amazon.co.jp/dp/B000A63EE8' },
      { label: 'エーワン 28179', url: 'https://www.amazon.co.jp/dp/B0000ACBOL' },
    ],
  },
  {
    title: 'A4 24面（3×8） 70×33.9mm',
    items: [
      { label: 'エーワン 28936', url: 'https://www.amazon.co.jp/dp/B000A63ERA' },
    ],
  },
  {
    title: 'A4 24面（3×8） 66×33.9mm',
    items: [
      { label: 'コクヨ KPC-HE1242-100', url: 'https://www.amazon.co.jp/dp/B00EOIRVME' },
      { label: 'コクヨ KPC-E1242-500', url: 'https://www.amazon.co.jp/dp/B00B53B43S' },
      { label: '中川製作所 UPRL24B-100', url: 'https://www.amazon.co.jp/dp/B076HLWYCY' },
    ],
  },
  {
    title: 'A4 44面（4×11） 48.3×25.4mm',
    items: [
      { label: 'エーワン 31516', url: 'https://www.amazon.co.jp/dp/B000K7JYPA' },
      { label: 'エーワン 31262', url: 'https://www.amazon.co.jp/dp/B000H6SWYI' },
      { label: 'エーワン 28944', url: 'https://www.amazon.co.jp/dp/B000A63ERU' },
    ],
  },
];

const downloadBase64File = (filename: string, contentBase64: string, mimeType: string) => {
  const binaryString = window.atob(contentBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const parseQrLabelHash = (hashValue: string): { tab: LabelTargetTab; partKind?: PartQrKind } => {
  const hash = hashValue.replace('#', '');
  if (hash === PART_CATEGORY_ORDER_HASH) {
    return { tab: 'part', partKind: 'partCategoryOrder' };
  }
  return {
    tab: VALID_TABS.includes(hash as LabelTargetTab) ? (hash as LabelTargetTab) : 'location',
  };
};

export function QrLabelPrintPage(): JSX.Element {
  const { message } = App.useApp();
  const pdfCreateMessageKey = 'qr-label-pdf-create';
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialTab = (): LabelTargetTab => {
    return parseQrLabelHash(location.hash).tab;
  };

  const getInitialPartQrKind = (): PartQrKind => {
    return parseQrLabelHash(location.hash).partKind ?? 'part';
  };

  const [activeTab, setActiveTab] = useState<LabelTargetTab>(getInitialTab());
  const [partQrKind, setPartQrKind] = useState<PartQrKind>(getInitialPartQrKind());

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    const parsed = parseQrLabelHash(location.hash);
    if (parsed.tab !== activeTab) {
      setActiveTab(parsed.tab);
    }
    if (parsed.partKind && parsed.partKind !== partQrKind) {
      setPartQrKind(parsed.partKind);
    }
  }, [activeTab, location.hash, partQrKind]);

  const handleTabChange = (key: string) => {
    setActiveTab(key as LabelTargetTab);
    navigate(`#${key}`, { replace: true });
  };

  const { data: templateData } = trpc.qrLabel.listTemplates.useQuery();
  const templates = useMemo(
    () => (templateData?.items ?? []) as QrLabelTemplate[],
    [templateData]
  );
  const [templateId, setTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId && templates.length > 0) {
      setTemplateId(templates[0].id);
    }
  }, [templateId, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) ?? null,
    [templateId, templates]
  );

  const labelsPerPage = selectedTemplate?.labelsPerPage ?? 0;
  const [startPosition, setStartPosition] = useState<number>(1);
  const [copies, setCopies] = useState<number>(1);
  const [mode, setMode] = useState<'qr' | 'qr+text'>('qr+text');

  useEffect(() => {
    if (labelsPerPage > 0 && startPosition > labelsPerPage) {
      setStartPosition(labelsPerPage);
    }
  }, [labelsPerPage, startPosition]);

  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<string[]>([]);
  const [selectedPartCategoryOrderIds, setSelectedPartCategoryOrderIds] = useState<string[]>([]);
  const activeSelection = activeTab === 'location'
    ? selectedLocationIds
    : activeTab === 'equipment'
      ? selectedEquipmentIds
      : activeTab === 'part'
        ? partQrKind === 'partCategoryOrder'
          ? selectedPartCategoryOrderIds
          : selectedPartIds
        : [];

  const [isPdfProcessing, setIsPdfProcessing] = useState(false);
  const createPdfMutation = trpc.qrLabel.createPdf.useMutation({
    onSuccess: (data) => {
      downloadBase64File(data.filename, data.contentBase64, data.mimeType);
      message.open({
        key: pdfCreateMessageKey,
        type: 'success',
        content: '印刷用PDFを作成しました',
      });
    },
    onError: (error) => {
      message.open({
        key: pdfCreateMessageKey,
        type: 'error',
        content: error.message || 'PDFの作成に失敗しました',
      });
    },
    onSettled: () => {
      setIsPdfProcessing(false);
    },
  });
  const isCreatingPdf = isPdfProcessing || createPdfMutation.isPending;

  const handleCreatePdf = () => {
    if (!templateId || activeSelection.length === 0 || isCreatingPdf) return;
    setIsPdfProcessing(true);
    message.open({
      key: pdfCreateMessageKey,
      type: 'loading',
      content: 'PDFを作成しています。完了までお待ちください。',
      duration: 0,
    });
    const activeTargetType =
      activeTab === 'part'
        ? partQrKind
        : activeTab === 'location' || activeTab === 'equipment'
          ? activeTab
          : null;
    if (!activeTargetType) {
      setIsPdfProcessing(false);
      message.open({
        key: pdfCreateMessageKey,
        type: 'error',
        content: '印刷対象の種類が正しく選択されていません。',
      });
      return;
    }
    createPdfMutation.mutate({
      templateId,
      items: activeSelection.map((id) => ({ id, type: activeTargetType })),
      startIndex: Math.max(0, startPosition - 1),
      copies,
      mode,
    });
  };

  const { data: branches = [] } = trpc.branch.list.useQuery({});
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedBranchId && branches.length > 0) {
      setSelectedBranchId(branches[0].id);
    }
  }, [branches, selectedBranchId]);

  useEffect(() => {
    setSelectedLocationIds([]);
  }, [selectedBranchId]);

  const [locationSearch, setLocationSearch] = useState('');
  const { data: locationRows = [], isLoading: isLocationLoading } = trpc.location.list.useQuery(
    { branchId: selectedBranchId ?? undefined },
    { enabled: Boolean(selectedBranchId) }
  );

  const filteredLocations = useMemo(() => {
    const term = locationSearch.trim().toLowerCase();
    if (!term) return locationRows as LocationRow[];
    return (locationRows as LocationRow[]).filter((row) =>
      row.name.toLowerCase().includes(term) || row.displayId.toLowerCase().includes(term)
    );
  }, [locationRows, locationSearch]);

  const locationColumns: ResponsiveColumnType<LocationRow>[] = [
    { title: 'ID', dataIndex: 'displayId', key: 'displayId', width: 160, mobileRole: 'tag', mobilePriority: 2 },
    { title: 'エリア名', dataIndex: 'name', key: 'name', mobileRole: 'title', mobilePriority: 1 },
  ];

  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentPageSize, setEquipmentPageSize] = useState(50);

  const { data: equipmentData, isLoading: isEquipmentLoading } = trpc.equipment.list.useQuery({
    page: equipmentPage,
    limit: equipmentPageSize,
    filter: {
      search: equipmentSearch || undefined,
      isActive: true,
      includeArchived: false,
    },
  });

  const equipmentRows = (equipmentData?.data ?? []) as EquipmentRow[];
  const equipmentTotal = equipmentData?.pagination?.total ?? 0;

  const equipmentColumns: ResponsiveColumnType<EquipmentRow>[] = [
    { title: 'ID', dataIndex: 'displayId', key: 'displayId', width: 160, mobileRole: 'tag', mobilePriority: 2 },
    { title: '設備名', dataIndex: 'name', key: 'name', mobileRole: 'title', mobilePriority: 1 },
  ];

  const [partSearch, setPartSearch] = useState('');
  const [partPage, setPartPage] = useState(1);
  const [partPageSize, setPartPageSize] = useState(50);

  const { data: partData, isLoading: isPartLoading } = trpc.parts.list.useQuery({
    page: partPage,
    limit: partPageSize,
    search: partSearch || undefined,
  });

  const partRows = (partData?.items ?? []) as PartRow[];
  const partTotal = partData?.pagination?.total ?? 0;

  const partColumns: ResponsiveColumnType<PartRow>[] = [
    { title: 'ID', dataIndex: 'displayId', key: 'displayId', width: 160, mobileRole: 'tag', mobilePriority: 2 },
    { title: '部品・消耗品名', dataIndex: 'name', key: 'name', mobileRole: 'title', mobilePriority: 1 },
  ];

  const { data: partCategories = [], isLoading: isPartCategoryLoading } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const partCategoryRows = partCategories as PartCategoryRow[];
  const partCategoryColumns: ResponsiveColumnType<PartCategoryRow>[] = [
    { title: 'カテゴリ名', dataIndex: 'categoryLabel', key: 'categoryLabel', mobileRole: 'title', mobilePriority: 1 },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      mobileRole: 'hidden',
      render: (_, row) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(row.id)}`)}
        >
          発注・消費記録を開く
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/QrLabelPrintPage.tsx" />
      <PageHeader
        title="QRコードシール印刷"
        subtitle="エリア・設備・部品の詳細ページURLをQRコードにして印刷用PDFを作成します"
      />

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Space direction="vertical" size={4}>
              <Text strong>ラベルテンプレート</Text>
              <Select
                value={templateId ?? undefined}
                onChange={(value) => setTemplateId(value)}
                style={{ width: '100%', maxWidth: 320 }}
                placeholder="テンプレートを選択"
              >
                {templates.map((template) => (
                  <Select.Option key={template.id} value={template.id}>
                    {template.name} ({template.grid.cols}x{template.grid.rows})
                  </Select.Option>
                ))}
              </Select>
            </Space>
            <Space direction="vertical" size={4}>
              <Text strong>開始位置</Text>
              <InputNumber
                min={1}
                max={labelsPerPage || 1}
                value={startPosition}
                onChange={(value) => setStartPosition(value ? Number(value) : 1)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                1ページあたり {labelsPerPage || 0} 枚
              </Text>
            </Space>
            <Space direction="vertical" size={4}>
              <Text strong>部数</Text>
              <InputNumber
                min={1}
                max={100}
                value={copies}
                onChange={(value) => setCopies(value ? Number(value) : 1)}
              />
            </Space>
            <Space direction="vertical" size={4}>
              <Text strong>表示モード</Text>
              <Radio.Group
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <Radio value="qr+text">ID + Name</Radio>
              </Radio.Group>
            </Space>
            <Space direction="vertical" size={4}>
              <Text strong>選択数</Text>
              <Text>{activeSelection.length} 件</Text>
            </Space>
            <Button
              type="primary"
              icon={<FilePdfOutlined aria-hidden="true" />}
              onClick={handleCreatePdf}
              disabled={!templateId || activeSelection.length === 0 || isCreatingPdf}
              loading={isCreatingPdf}
            >
              {isCreatingPdf ? 'PDF作成中' : 'PDF作成'}
            </Button>
          </Space>
          {isCreatingPdf && (
            <Alert
              type="info"
              showIcon
              message="PDFを作成しています"
              description="書き出し件数が多い場合は時間がかかることがあります。完了までお待ちください。"
            />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            表示モードは「ID + Name」が標準です。開始位置でラベル用紙の残り枠を調整できます。
          </Text>
        </Space>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'location',
            label: (
              <span>
                <EnvironmentOutlined /> エリア
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                  <Input
                    placeholder="エリア名またはIDで検索"
                    prefix={<SearchOutlined />}
                    value={locationSearch}
                    onChange={(event) => setLocationSearch(event.target.value)}
                    allowClear
                    style={{ width: '100%', maxWidth: 320 }}
                  />
                  <Select
                    value={selectedBranchId ?? undefined}
                    onChange={(value) => setSelectedBranchId(value)}
                    style={{ width: '100%', maxWidth: 320 }}
                    placeholder="拠点を選択"
                  >
                    {(branches ?? []).map((branch) => (
                      <Select.Option key={branch.id} value={branch.id}>
                        {branch.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
                <ResponsiveTable<LocationRow>
                  rowKey="id"
                  columns={locationColumns}
                  dataSource={filteredLocations}
                  loading={isLocationLoading}
                  size="small"
                  rowSelection={{
                    selectedRowKeys: selectedLocationIds,
                    onChange: (keys) => setSelectedLocationIds(keys.map(String)),
                    preserveSelectedRowKeys: true,
                  }}
                  mobileCardConfig={{
                    titleField: 'name',
                  }}
                  pagination={{ pageSize: 100, showSizeChanger: false }}
                />
              </Space>
            ),
          },
          {
            key: 'equipment',
            label: (
              <span>
                <ToolOutlined /> 設備
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Input
                  placeholder="設備名またはIDで検索"
                  prefix={<SearchOutlined />}
                  value={equipmentSearch}
                  onChange={(event) => {
                    setEquipmentSearch(event.target.value);
                    setEquipmentPage(1);
                  }}
                  allowClear
                  style={{ width: '100%', maxWidth: 320 }}
                />
                <ResponsiveTable<EquipmentRow>
                  rowKey="id"
                  columns={equipmentColumns}
                  dataSource={equipmentRows}
                  loading={isEquipmentLoading}
                  size="small"
                  rowSelection={{
                    selectedRowKeys: selectedEquipmentIds,
                    onChange: (keys) => setSelectedEquipmentIds(keys.map(String)),
                    preserveSelectedRowKeys: true,
                  }}
                  mobileCardConfig={{
                    titleField: 'name',
                  }}
                  pagination={{
                    current: equipmentPage,
                    pageSize: equipmentPageSize,
                    total: equipmentTotal,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                      setEquipmentPage(page);
                      setEquipmentPageSize(pageSize || 50);
                    },
                  }}
                />
              </Space>
            ),
          },
          {
            key: 'part',
            label: (
              <span>
                <AppstoreOutlined /> 部品・消耗品
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio.Group
                  value={partQrKind}
                  onChange={(event) => setPartQrKind(event.target.value)}
                  style={{ display: 'flex', flexWrap: 'wrap' }}
                >
                  <Radio.Button value="part">部品確認用QR</Radio.Button>
                  <Radio.Button value="partCategoryOrder">カテゴリ発注QR</Radio.Button>
                </Radio.Group>

                {partQrKind === 'part' ? (
                  <>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      読み取ると個別の部品・消耗品詳細が開きます。
                    </Text>
                    <Input
                      placeholder="部品・消耗品名またはIDで検索"
                      prefix={<SearchOutlined />}
                      value={partSearch}
                      onChange={(event) => {
                        setPartSearch(event.target.value);
                        setPartPage(1);
                      }}
                      allowClear
                      style={{ width: '100%', maxWidth: 320 }}
                    />
                    <ResponsiveTable<PartRow>
                      rowKey="id"
                      columns={partColumns}
                      dataSource={partRows}
                      loading={isPartLoading}
                      size="small"
                      rowSelection={{
                        selectedRowKeys: selectedPartIds,
                        onChange: (keys) => setSelectedPartIds(keys.map(String)),
                        preserveSelectedRowKeys: true,
                      }}
                      mobileCardConfig={{
                        titleField: 'name',
                      }}
                      pagination={{
                        current: partPage,
                        pageSize: partPageSize,
                        total: partTotal,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                          setPartPage(page);
                          setPartPageSize(pageSize || 50);
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      message="カテゴリ発注QR"
                      description="読み取るとこのカテゴリの部品一覧が開きます"
                    />
                    <ResponsiveTable<PartCategoryRow>
                      rowKey="id"
                      columns={partCategoryColumns}
                      dataSource={partCategoryRows}
                      loading={isPartCategoryLoading}
                      size="small"
                      rowSelection={{
                        selectedRowKeys: selectedPartCategoryOrderIds,
                        onChange: (keys) => setSelectedPartCategoryOrderIds(keys.map(String)),
                        preserveSelectedRowKeys: true,
                      }}
                      mobileCardConfig={{
                        titleField: 'categoryLabel',
                        actions: (row) => (
                          <Button
                            type="link"
                            onClick={() => navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(row.id)}`)}
                          >
                            発注・消費記録を開く
                          </Button>
                        ),
                      }}
                      pagination={{ pageSize: 100, showSizeChanger: false }}
                    />
                  </>
                )}
              </Space>
            ),
          },
          {
            key: 'sheets',
            label: (
              <span>
                <LinkOutlined /> 印刷シール購入
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Amazonの購入リンクです。必要なラベルサイズに合わせて選択してください。
                </Text>
                {PRINT_SHEET_GROUPS.map((group) => (
                  <Card key={group.title} size="small">
                    <Space direction="vertical" size={6}>
                      <Text strong>{group.title}</Text>
                      <Space direction="vertical" size={4}>
                        {group.items.map((item) => (
                          <Link key={item.url} href={item.url} target="_blank" rel="noreferrer">
                            {item.label}
                          </Link>
                        ))}
                      </Space>
                    </Space>
                  </Card>
                ))}
              </Space>
            ),
          },
        ]}
      />
    </PageContainer>
  );
}
